import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiGrid, FiBarChart2, FiCalendar, FiEdit2, FiDollarSign, FiLayers } from 'react-icons/fi';
import BoardTable from '../components/BoardTable';
import BoardCalendar from '../components/BoardCalendar';
import BoardGantt from '../components/BoardGantt';
import BoardFinance from '../components/BoardFinance';
import EditTaskModal from '../components/EditTaskModal';
import SharedLists from './SharedLists';
import './BoardDetail.css';

const BoardDetail = ({ token, user }) => {
  const { boardId } = useParams();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');
  const [editingName, setEditingName] = useState(false);
  const [financeTaskId, setFinanceTaskId] = useState(null);
  const [financeRefreshKey, setFinanceRefreshKey] = useState(0);

  const refreshFinance = () => setFinanceRefreshKey((k) => k + 1);

  const findTaskById = (id) => {
    if (!board) return null;
    for (const g of board.groups || []) {
      for (const i of g.items || []) {
        if (i.id === id) return i;
      }
    }
    return null;
  };

  useEffect(() => {
    fetchBoard();
  }, [boardId]);

  const fetchBoard = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards/${boardId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBoard(data.board);
      }
    } catch (error) {
      console.error('Failed to fetch board:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBoardNameEdit = async (newName) => {
    if (!newName.trim()) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards/${boardId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName })
      });
      if (response.ok) {
        setBoard({ ...board, name: newName });
        setEditingName(false);
      }
    } catch (error) {
      console.error('Failed to update board name:', error);
    }
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  if (!board) {
    return <div>Board not found</div>;
  }

  return (
    <div className="board-detail">
      <div className="board-detail-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {editingName ? (
              <input
                type="text"
                defaultValue={board.name}
                autoFocus
                onBlur={(e) => handleBoardNameEdit(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleBoardNameEdit(e.target.value);
                  }
                }}
                className="board-name-input"
                data-testid="board-name-input"
              />
            ) : (
              <>
                <h1 className="board-detail-title">{board.name}</h1>
                <button 
                  className="edit-board-name-btn"
                  onClick={() => setEditingName(true)}
                  data-testid="edit-board-name-button"
                >
                  <FiEdit2 size={18} />
                </button>
              </>
            )}
          </div>
          <div className="board-detail-meta">
            <span>{board.client}</span>
            <span>•</span>
            <span>{board.projectType}</span>
          </div>
        </div>
        <div className="board-view-switcher">
          <button
            className={`view-btn ${view === 'table' ? 'active' : ''}`}
            onClick={() => setView('table')}
            data-testid="table-view-button"
          >
            <FiGrid size={18} />
            <span>Table</span>
          </button>
          <button
            className={`view-btn ${view === 'gantt' ? 'active' : ''}`}
            onClick={() => setView('gantt')}
            data-testid="gantt-view-button"
          >
            <FiBarChart2 size={18} />
            <span>Gantt</span>
          </button>
          <button
            className={`view-btn ${view === 'calendar' ? 'active' : ''}`}
            onClick={() => setView('calendar')}
            data-testid="calendar-view-button"
          >
            <FiCalendar size={18} />
            <span>Calendar</span>
          </button>
          <button
            className={`view-btn ${view === 'finance' ? 'active' : ''}`}
            onClick={() => setView('finance')}
            data-testid="finance-view-button"
          >
            <FiDollarSign size={18} />
            <span>Client Statement</span>
          </button>
          <button
            className={`view-btn ${view === 'shared-lists' ? 'active' : ''}`}
            onClick={() => setView('shared-lists')}
            data-testid="shared-lists-view-button"
          >
            <FiLayers size={18} />
            <span>Shared Lists</span>
          </button>
        </div>
      </div>

      {view === 'table' && (
        <BoardTable
          board={board}
          token={token}
          user={user}
          onUpdate={fetchBoard}
        />
      )}
      {view === 'gantt' && (
        <BoardGantt
          board={board}
          token={token}
          onUpdate={fetchBoard}
        />
      )}
      {view === 'calendar' && (
        <BoardCalendar
          board={board}
          token={token}
          onUpdate={fetchBoard}
        />
      )}
      {view === 'finance' && (
        <BoardFinance
          boardId={boardId}
          board={board}
          token={token}
          onTaskClick={(id) => setFinanceTaskId(id)}
          onBoardUpdate={fetchBoard}
          refreshKey={financeRefreshKey}
        />
      )}
      {view === 'shared-lists' && (
        <SharedLists token={token} boardId={boardId} boardName={board.name} embedded />
      )}

      {financeTaskId && (
        <EditTaskModal
          isOpen={!!financeTaskId}
          task={findTaskById(financeTaskId)}
          token={token}
          boardId={boardId}
          boardBaseCurrency={board?.baseCurrency || 'USD'}
          initialTab="finance"
          onClose={() => { setFinanceTaskId(null); refreshFinance(); }}
          onUpdate={() => { fetchBoard(); refreshFinance(); }}
        />
      )}
    </div>
  );
};

export default BoardDetail;